'use client';

import { useState } from 'react';
import {
  ArrowLeft,
  LayoutDashboard,
  Building2,
  ShoppingCart,
  Package,
  Receipt,
  ClipboardList,
} from 'lucide-react';
import { BuyingDashboard } from './components/BuyingDashboard';
import { SupplierListPage } from './components/SupplierList';
import { PurchaseOrderListPage } from './components/PurchaseOrderList';
import { PurchaseReceiptListPage } from './components/PurchaseReceiptList';
import { PurchaseInvoiceListPage } from './components/PurchaseInvoiceList';
import { MaterialRequestListPage } from './components/MaterialRequestList';

export type BuyingPage =
  | 'dashboard'
  | 'suppliers'
  | 'orders'
  | 'receipts'
  | 'invoices'
  | 'requests';

interface BuyingModuleProps {
  onBack?: () => void;
}

const menuItems: { id: BuyingPage; label: string; icon: typeof LayoutDashboard }[] = [
  { id: 'dashboard', label: '대시보드', icon: LayoutDashboard },
  { id: 'suppliers', label: '공급업체', icon: Building2 },
  { id: 'orders', label: '발주서', icon: ShoppingCart },
  { id: 'receipts', label: '입고', icon: Package },
  { id: 'invoices', label: '청구서', icon: Receipt },
  { id: 'requests', label: '자재요청', icon: ClipboardList },
];

export function BuyingModule({ onBack }: BuyingModuleProps) {
  const [currentPage, setCurrentPage] = useState<BuyingPage>('dashboard');

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <BuyingDashboard onNavigate={setCurrentPage} />;
      case 'suppliers':
        return <SupplierListPage />;
      case 'orders':
        return <PurchaseOrderListPage />;
      case 'receipts':
        return <PurchaseReceiptListPage />;
      case 'invoices':
        return <PurchaseInvoiceListPage />;
      case 'requests':
        return <MaterialRequestListPage />;
      default:
        return <BuyingDashboard onNavigate={setCurrentPage} />;
    }
  };

  return (
    <div className="flex flex-col h-full -m-6">
      {/* 상단 네비게이션 */}
      <div className="flex items-center gap-2 px-4 py-3 bg-card border-b border-border">
        {onBack && (
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-accent"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <h1 className="text-xl font-bold mr-6">구매 관리</h1>

        {/* 탭 메뉴 */}
        <div className="flex items-center gap-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentPage === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setCurrentPage(item.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 페이지 컨텐츠 */}
      <div className="flex-1 overflow-auto p-6">
        {renderPage()}
      </div>
    </div>
  );
}
